import React from 'react';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
  /** Enable hover effects */
  hoverEffect?: boolean;
  /** Card variant */
  variant?: 'default' | 'elevated' | 'outlined' | 'interactive';
  /** Padding preset */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Click handler for interactive cards */
  onClick?: (event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => void;
}

export const Card: React.FC<CardProps> = ({
  children,
  className = '',
  hoverEffect = true,
  variant = 'default',
  padding = 'md',
  onClick,
  ...props
}) => {
  // Determine if this card should be keyboard-accessible
  const isInteractive = variant === 'interactive' || onClick;

  // Handle keyboard events for interactive cards (Enter/Space activation)
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isInteractive && onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick(event);
    }
  };

  const baseStyles = [
    'bg-st-card border border-slate-800 rounded-lg',
    // Smooth transitions for all micro-interactions
    'transition-all duration-300 ease-out',
  ].join(' ');

  const paddingStyles = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  const variantStyles = {
    default: '',
    elevated: 'shadow-lg shadow-black/20',
    outlined: 'border-2',
    interactive: 'cursor-pointer',
  };

  const hoverStyles = hoverEffect
    ? [
        // Border glow on hover
        'hover:border-st-primary/50',
        // Subtle shadow expansion
        'hover:shadow-xl hover:shadow-st-primary/10',
        // Gentle lift effect
        'hover:-translate-y-1',
        // Inner glow effect using gradient
        'hover:bg-gradient-to-br hover:from-st-card hover:to-st-card/95',
      ].join(' ')
    : '';

  // Additional interactive styles for clickable cards (applies when interactive variant OR has onClick)
  const interactiveStyles = isInteractive
    ? [
        'active:scale-[0.99]',
        'active:shadow-md',
        'focus-visible:ring-2 focus-visible:ring-st-primary focus-visible:ring-offset-2 focus-visible:ring-offset-st-dark',
        'focus:outline-none',
      ].join(' ')
    : '';

  // Accessibility attributes for interactive cards
  const accessibilityProps = isInteractive
    ? {
        tabIndex: 0,
        role: 'button' as const,
        onKeyDown: handleKeyDown,
        onClick,
      }
    : {};

  return (
    <div
      className={`${baseStyles} ${paddingStyles[padding]} ${variantStyles[variant]} ${hoverStyles} ${interactiveStyles} ${className}`}
      {...accessibilityProps}
      {...props}
    >
      {children}
    </div>
  );
};

